/**
 * Shared constants and small pure helpers for Meta Ads page components
 * (`components/pages/metaAds/`).
 */

// --- Performance: Meta Marketing API `date_preset` (insights) ---

/**
 * @see https://developers.facebook.com/docs/marketing-api/insights/parameters/v2.3
 */
export const META_ADS_DATE_PRESET_OPTIONS = [
  { value: "yesterday", i18nKey: "datePresetYesterday" },
  { value: "last_7d", i18nKey: "datePresetLast7d" },
  { value: "last_14d", i18nKey: "datePresetLast14d" },
  { value: "last_30d", i18nKey: "datePresetLast30d" },
  { value: "last_90d", i18nKey: "datePresetLast90d" },
  { value: "this_month", i18nKey: "datePresetThisMonth" },
];

export function formatMetaAdsMoneyFromCents(cents) {
  if (cents == null) {
    return "—";
  }
  const n = Number(cents) / 100;
  return n.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

export function formatMetaAdsRoas(v) {
  if (v == null || Number.isNaN(Number(v))) {
    return "—";
  }
  return `${Number(v).toFixed(2)}×`;
}

// --- Create campaign: Meta campaign objective API values ---

export const META_ADS_CAMPAIGN_OBJECTIVE_TRAFFIC = "OUTCOME_TRAFFIC";
export const META_ADS_CAMPAIGN_OBJECTIVE_SALES = "OUTCOME_SALES";

/** Maps Meta Campaign API `objective` string → `performance.*` i18n key (without namespace). */
const META_ADS_OBJECTIVE_I18N_KEYS = {
  OUTCOME_TRAFFIC: "objectiveLabelOutcomeTraffic",
  OUTCOME_SALES: "objectiveLabelOutcomeSales",
  OUTCOME_AWARENESS: "objectiveLabelOutcomeAwareness",
  OUTCOME_ENGAGEMENT: "objectiveLabelOutcomeEngagement",
  OUTCOME_LEADS: "objectiveLabelOutcomeLeads",
  OUTCOME_APP_PROMOTION: "objectiveLabelOutcomeAppPromotion",
};

const META_ADS_OPTIMIZATION_GOAL_I18N_KEYS = {
  LINK_CLICKS: "optimizationGoalLinkClicks",
  LANDING_PAGE_VIEWS: "optimizationGoalLandingPageViews",
  OFFSITE_CONVERSIONS: "optimizationGoalOffsiteConversions",
  VALUE: "optimizationGoalValue",
  IMPRESSIONS: "optimizationGoalImpressions",
  REACH: "optimizationGoalReach",
  POST_ENGAGEMENT: "optimizationGoalPostEngagement",
  PAGE_LIKES: "optimizationGoalPageLikes",
  LEAD_GENERATION: "optimizationGoalLeadGeneration",
  CONVERSATIONS: "optimizationGoalConversations",
  THRUPLAY: "optimizationGoalThruPlay",
};

/**
 * Human-readable campaign objective for tables (translated when known; title case otherwise).
 * @param {string|null|undefined} objective - Raw value from Meta API
 * @param {(key: string) => string} t - `useTranslation("metaAds").t`
 */
export function formatMetaAdsCampaignObjective(objective, t) {
  const raw = objective != null ? String(objective).trim() : "";
  if (!raw) {
    return "—";
  }
  const i18nKey = META_ADS_OBJECTIVE_I18N_KEYS[raw];
  if (i18nKey) {
    return t(`performance.${i18nKey}`);
  }
  return raw
    .replace(/^OUTCOME_/i, "")
    .split("_")
    .filter(Boolean)
    .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
    .join(" ");
}

export function formatMetaAdsAdSetOptimizationGoal(goal, t) {
  const raw = goal != null ? String(goal).trim() : "";
  if (!raw) {
    return "—";
  }
  const i18nKey = META_ADS_OPTIMIZATION_GOAL_I18N_KEYS[raw];
  if (i18nKey) {
    return t(`performance.${i18nKey}`);
  }
  return raw
    .split("_")
    .filter(Boolean)
    .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
    .join(" ");
}

/** Marketing API `effective_status` / `status`: switch "on" for anything except paused, deleted, archived. */
export function metaAdsCampaignIsEffectivelyOn(status) {
  const s = String(status ?? "")
    .trim()
    .toUpperCase();
  if (!s) {
    return false;
  }
  if (s === "PAUSED" || s === "DELETED" || s === "ARCHIVED") {
    return false;
  }
  return true;
}

export function metaAdsCampaignStatusCanToggle(status) {
  const s = String(status ?? "")
    .trim()
    .toUpperCase();
  if (!s) {
    return false;
  }
  return s !== "DELETED" && s !== "ARCHIVED";
}

// --- Business picker (useBusinesses) ---

export const META_ADS_BUSINESS_PICKER_PAGE_SIZE = 5;

/** Aligned with BusinessDropdown / BusinessSelectionModal */
export const META_ADS_BUSINESS_SEARCH_DEBOUNCE_MS = 300;

// --- Creative feed preview (toggle Instagram / Facebook mock; Graph uses META_AD_PREVIEW_FORMAT_IDS) ---

export const META_ADS_PREVIEW_PLATFORM_INSTAGRAM = "instagram";
export const META_ADS_PREVIEW_PLATFORM_FACEBOOK = "facebook";

/**
 * Allowed Marketing API ``ad_format`` values for dashboard previews.
 * Same list is enforced in `services/meta-ads` so proxy calls stay within this set (limits Graph rate use).
 * @type {readonly string[]}
 */
export const META_AD_PREVIEW_FORMAT_IDS = Object.freeze([
  "MOBILE_FEED_STANDARD",
  "FACEBOOK_REELS_MOBILE",
  "DESKTOP_FEED_STANDARD",
]);

/**
 * Tuned width/height for the embedded Graph preview iframe. Meta’s HTML often uses
 * 335×450 (mobile feed), 274×213 (Reels), 540×690 (desktop); those values leave excess
 * letterboxing on feeds and clip Reels — these sizes replace them when rendering.
 * @type {Readonly<Record<string, { width: number; height: number }>>}
 */
export const META_AD_PREVIEW_EMBED_DIMENSIONS = Object.freeze({
  MOBILE_FEED_STANDARD: { width: 335, height: 360 },
  FACEBOOK_REELS_MOBILE: { width: 274, height: 560 },
  DESKTOP_FEED_STANDARD: { width: 540, height: 560 },
});

function normalizeMetaAdPreviewFormatId(adFormat) {
  return (
    String(adFormat ?? "")
      .trim()
      .toUpperCase() || "MOBILE_FEED_STANDARD"
  );
}

/**
 * @param {string|null|undefined} adFormat
 * @returns {{ width: number; height: number }}
 */
export function getMetaAdPreviewEmbedDimensions(adFormat) {
  const u = normalizeMetaAdPreviewFormatId(adFormat);
  const spec = META_AD_PREVIEW_EMBED_DIMENSIONS[u];
  if (spec) {
    return { width: spec.width, height: spec.height };
  }
  return { width: 335, height: 450 };
}

/**
 * Replaces width/height on the first iframe in the Graph preview HTML fragment.
 * @param {string|null|undefined} rawBody
 * @param {string|null|undefined} adFormat
 */
export function rewriteMetaPreviewIframeHtmlDimensions(rawBody, adFormat) {
  const raw = String(rawBody ?? "").trim();
  if (!raw || !/^<iframe\b/i.test(raw)) {
    return raw;
  }
  const { width, height } = getMetaAdPreviewEmbedDimensions(adFormat);
  let out = raw;
  if (/\bwidth=["']?\d+["']?/i.test(out)) {
    out = out.replace(/\bwidth=["']?\d+["']?/i, `width="${width}"`);
  } else {
    out = out.replace(/<iframe\b/i, `<iframe width="${width}"`);
  }
  if (/\bheight=["']?\d+["']?/i.test(out)) {
    out = out.replace(/\bheight=["']?\d+["']?/i, `height="${height}"`);
  } else {
    out = out.replace(/<iframe\b/i, `<iframe height="${height}"`);
  }
  return out;
}

/**
 * Readable label for a Meta ``ad_format`` enum (locale-neutral title case).
 * @param {string|null|undefined} id
 */
export function humanizeMetaAdPreviewFormatId(id) {
  const raw = id != null ? String(id).trim() : "";
  if (!raw) {
    return "—";
  }
  return raw
    .split("_")
    .filter(Boolean)
    .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
    .join(" ");
}

// --- Destination URL: Phase E store dimension (`utm_content=store_{id}`) ---

/**
 * Reads Finitless store id from `utm_content` when it matches `store_<id>` (same contract as meta-ads `utm.py`).
 * @param {string|null|undefined} urlString
 * @returns {string|null}
 */
export function parseMetaAdsStoreIdFromDestinationUrl(urlString) {
  const raw = urlString != null ? String(urlString).trim() : "";
  if (!raw) {
    return null;
  }
  try {
    const u = new URL(raw);
    const content = (u.searchParams.get("utm_content") || "").trim();
    const m = /^store_(.+)$/i.exec(content);
    if (!m) {
      return null;
    }
    const id = String(m[1]).trim();
    return id !== "" ? id : null;
  } catch {
    return null;
  }
}

/**
 * Sets or overwrites `utm_content=store_{storeId}` on an absolute http(s) URL.
 * @param {string|null|undefined} urlString
 * @param {string|null|undefined} storeId
 * @returns {string}
 */
export function ensureMetaAdsDestinationUrlStoreUtm(urlString, storeId) {
  const sid = storeId != null ? String(storeId).trim() : "";
  const raw = urlString != null ? String(urlString).trim() : "";
  if (!raw || !sid) {
    return raw;
  }
  const u = new URL(raw);
  u.searchParams.set("utm_content", `store_${sid}`);
  return u.toString();
}

/**
 * @param {string|null|undefined} urlString
 * @param {string|null|undefined} storeId
 */
export function metaAdsDestinationUrlHasCorrectStoreUtm(urlString, storeId) {
  const sid = storeId != null ? String(storeId).trim() : "";
  if (!sid) {
    return true;
  }
  const parsed = parseMetaAdsStoreIdFromDestinationUrl(urlString);
  return parsed === sid;
}

const META_ADS_HTTP_URL_RE = /^https?:\/\//i;

/**
 * Draft creative fields valid for Meta ``generatepreviews`` (link creative).
 * @param {{ destinationUrl?: string, imageUrl?: string, primaryText?: string, pageId?: string }|null|undefined} draft
 */
export function metaAdsLivePreviewDraftReady(draft) {
  if (!draft || typeof draft !== "object") {
    return false;
  }
  const primaryText = String(draft.primaryText ?? "").trim();
  const pageId = String(draft.pageId ?? "").trim();
  const destinationUrl = String(draft.destinationUrl ?? "").trim();
  const imageUrl = String(draft.imageUrl ?? "").trim();
  return Boolean(
    primaryText &&
    pageId &&
    imageUrl &&
    META_ADS_HTTP_URL_RE.test(destinationUrl) &&
    META_ADS_HTTP_URL_RE.test(imageUrl),
  );
}

// --- Connect page: “What’s next” cards ---

export const META_ADS_CONNECT_PAGE_HIGHLIGHT_CARDS = [
  {
    icon: "Rocket",
    titleKey: "connect.next1Title",
    bodyKey: "connect.next1Body",
  },
  {
    icon: "BarChart3",
    titleKey: "connect.next2Title",
    bodyKey: "connect.next2Body",
  },
  {
    icon: "Link2",
    titleKey: "connect.next3Title",
    bodyKey: "connect.next3Body",
  },
];
